import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useReducedMotion,
} from "framer-motion";
import { ArrowRight } from "lucide-react";

// Ingredient Spotlight — three official Riversol actives. On desktop this is
// a pinned horizontal-scroll "filmstrip" (vertical scroll drives the banners
// sideways, then releases once the last one is fully revealed). On mobile
// (and with reduced-motion) it falls back to a plain swipe carousel. All
// names, copy and imagery mirror riversol.com's live homepage banners.
const INGREDIENTS = [
  {
    name: "Hinokitiol",
    description:
      "Our signature antioxidant, found in the Pacific red cedar, visibly brightens dark spots, improves skin clarity and evens skin texture.",
    href: "https://www.riversol.com/blogs/riversol/the-natural-benefits-of-beta-thujaplicin",
    shop: "https://www.riversol.com/search?q=hinokitiol",
    image: "https://www.riversol.com/cdn/shop/files/hinokitiol.png?v=1772154769&width=1600",
    accent: "#8fd6b4",
  },
  {
    name: "Vitamin C",
    description:
      "Riversol's photostabilized Vitamin C delivers antioxidant protection while brightening skin and reducing dark spots — 10x more stable than traditional forms.",
    href: "https://www.riversol.com/pages/vitamin-c-and-skin-health",
    shop: "https://www.riversol.com/search?q=vitamin%20c",
    image: "https://www.riversol.com/cdn/shop/files/vitamin_c_9cbe2d53-d1c5-44f4-9939-f6b748070640.png?v=1772845187&width=1600",
    accent: "#ffcf99",
  },
  {
    name: "Retinol",
    description:
      "Retinol visibly improves the look of dark spots, fine lines and wrinkles. Every Riversol retinol is precision-formulated for sensitive skin.",
    href: "https://www.riversol.com/blogs/riversol/the-dos-and-don-ts-of-using-retinol-safely-in-the-summertime",
    shop: "https://www.riversol.com/search?q=retinol",
    image: "https://www.riversol.com/cdn/shop/files/retinol_1_c0071053-6d85-4253-bdb0-51ff50f4bfec.jpg?v=1773689761&width=1600",
    accent: "#a9c8f2",
  },
];

const slug = (n) => n.toLowerCase().replace(/\s+/g, "-");

// Shared banner content (sharp corners, bright image, light bottom gradient
// for legibility, accent Shop CTA + secondary Learn more).
const Banner = ({ item }) => (
  <div className="relative w-full h-full overflow-hidden bg-neutral-900">
    <img
      src={item.image}
      alt={item.name}
      className="absolute inset-0 w-full h-full object-cover"
      draggable={false}
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent pointer-events-none" />
    <div className="absolute inset-x-0 bottom-0 p-7 lg:p-12">
      <span className="font-mono text-[10px] tracking-[0.28em] uppercase" style={{ color: item.accent }}>
        Ingredient
      </span>
      <h3 className="mt-3 text-white text-[40px] lg:text-[60px] leading-[0.98] tracking-[-0.02em]">
        {item.name}
      </h3>
      <p className="mt-4 text-white/85 text-[14px] lg:text-[15px] leading-relaxed max-w-[520px]">
        {item.description}
      </p>
      <div className="mt-7 flex items-center gap-6 flex-wrap">
        <a
          href={item.shop}
          data-testid={`ingredient-shop-${slug(item.name)}`}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[12px] tracking-[0.14em] uppercase font-semibold text-neutral-900 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.5)] transition-transform duration-300 hover:-translate-y-0.5"
          style={{ backgroundColor: item.accent }}
        >
          Shop {item.name}
          <ArrowRight size={15} strokeWidth={2} />
        </a>
        <a
          href={item.href}
          data-testid={`ingredient-cta-${slug(item.name)}`}
          className="inline-flex items-center gap-2 text-white/90 text-[12px] tracking-[0.16em] uppercase font-medium border-b border-white/40 pb-1 hover:border-white transition-colors"
        >
          Learn more
          <ArrowRight size={14} strokeWidth={1.8} />
        </a>
      </div>
    </div>
  </div>
);

const Heading = () => (
  <div>
    <motion.h2
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.05] tracking-[-0.02em] text-neutral-900 max-w-[640px]"
    >
      The actives behind <span className="italic text-riversol-blue">the results.</span>
    </motion.h2>
  </div>
);

// ---- Desktop: pinned horizontal scroll ----
const DesktopPinned = () => {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], ["0vw", "-100vw"]);
  const barScale = useTransform(scrollYProgress, [0, 1], [1 / INGREDIENTS.length, 1]);
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(INGREDIENTS.length - 1, Math.round(v * (INGREDIENTS.length - 1))));
  });

  return (
    <div ref={wrapRef} className="hidden lg:block relative" style={{ height: "260vh" }} data-testid="ingredient-pinned">
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col bg-riversol-paper">
        <div className="px-10 pt-28 pb-6 flex items-end justify-between gap-8">
          <Heading />
          <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500 tabular-nums shrink-0">
            {String(active + 1).padStart(2, "0")} / {String(INGREDIENTS.length).padStart(2, "0")}
          </span>
        </div>

        <div className="flex-1 min-h-0 pb-10">
          <motion.div className="flex h-full items-stretch" style={{ x }}>
            <div className="shrink-0 w-[2vw]" aria-hidden />
            {INGREDIENTS.map((item) => (
              <div key={item.name} className="relative shrink-0 h-full w-[64vw] mr-[2vw]">
                <Banner item={item} />
              </div>
            ))}
          </motion.div>
        </div>

        <div className="px-10 pb-8 flex items-center gap-4">
          <div className="relative h-px flex-1 bg-neutral-300 overflow-hidden">
            <motion.span
              className="absolute inset-y-0 left-0 w-full bg-riversol-blue origin-left"
              style={{ scaleX: barScale }}
            />
          </div>
          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400 whitespace-nowrap">
            Scroll to explore
          </span>
        </div>
      </div>
    </div>
  );
};

// ---- Mobile / reduced-motion: swipe carousel ----
const MobileCarousel = ({ show }) => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: false, align: "start", containScroll: "trimSnaps" });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    return () => {
      embla.off("select", onSelect);
      embla.off("reInit", onSelect);
    };
  }, [embla, onSelect]);

  return (
    <section className={`${show ? "" : "lg:hidden"} bg-riversol-paper py-20`} data-testid="home-ingredient-spotlight">
      <div className="max-w-[1600px] mx-auto">
        <div className="px-6 mb-8">
          <Heading />
        </div>
        <div className="overflow-hidden cursor-grab active:cursor-grabbing pl-6 pr-2" ref={emblaRef}>
          <div className="flex">
            {INGREDIENTS.map((item) => (
              <div
                key={item.name}
                className="relative shrink-0 grow-0 basis-[88%] sm:basis-[72%] pl-4"
              >
                <div className="relative overflow-hidden bg-neutral-900" style={{ height: "min(64vh, 560px)" }}>
                  <Banner item={item} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 mt-7 flex items-center gap-3">
          {INGREDIENTS.map((item, idx) => (
            <button
              key={item.name}
              type="button"
              onClick={() => embla && embla.scrollTo(idx)}
              aria-label={`Go to ${item.name}`}
              data-testid={`ingredient-dot-${idx}`}
              className="py-2"
            >
              <span
                className={`block h-[2px] transition-all duration-500 ${
                  idx === selected ? "w-10 bg-riversol-blue" : "w-5 bg-neutral-300"
                }`}
              />
            </button>
          ))}
          <span className="ml-2 font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400 tabular-nums">
            {String(selected + 1).padStart(2, "0")} / {String(INGREDIENTS.length).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
};

export const IngredientSpotlight = () => {
  const reduced = useReducedMotion();
  return (
    <>
      {!reduced && <DesktopPinned />}
      {/* Mobile always; also acts as the desktop fallback when reduced-motion */}
      <MobileCarousel show={!!reduced} />
    </>
  );
};

export default IngredientSpotlight;
